import React, { ReactNode, useMemo, useState } from 'react'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import startCase from 'lodash/startCase'
import { LuChevronDown, LuChevronUp, LuChevronsUpDown } from 'react-icons/lu'

import Drilldown from '@/insights/components/Drilldown'
import { InsightConfigurationType } from '@/types/dashboard-types'

import { RenderCell } from './CellRenderers'
import { createSortingFn } from './sortingUtils'

import MapModal, { MapLocation } from '../map/MapModal'
import { cx } from '../utils'

enum TableVariationStyle {
  secondary = 'secondary',
}

export type DrilldownCell = {
  id: string
  value: string | number | boolean
  configuration: InsightConfigurationType
  className?: string
}

export type AddressCell = {
  id: string
  address: string
  lat: number
  lng: number
  className?: string
  companyId: number
  markerContent?: ReactNode
}

export type DateCell = {
  id: string
  date: Date | string
  display: React.ReactNode
  className?: string
}

export interface SimpleTableBodyType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

// New interface for header configuration
export interface TableHeader {
  key: string
  title?: string
  icon?: React.ReactNode
}

// Type that accepts either string or TableHeader object
export type HeaderConfig = string | TableHeader

export interface SimpleTableProps {
  headers: HeaderConfig[]
  body?: SimpleTableBodyType[]
  keysToIgnore?: string[]
  title?: string
  variation?: keyof typeof TableVariationStyle
  headerClassNames?: Record<string, string>
  cellClassNames?: Record<string, string>
  className?: string
  containerClassName?: string
}

// Helper function to normalize headers to consistent format
const normalizeHeaders = (headers: HeaderConfig[]): TableHeader[] => {
  return headers.map((header) => {
    if (typeof header === 'string') {
      return { key: header, id: header, title: header }
    }
    return {
      key: header.key,
      id: header.title,
      title: header.title || header.key,
      icon: header.icon,
    }
  })
}

export default function SimpleTable({
  headers,
  body,
  keysToIgnore,
  title,
  variation,
  headerClassNames = {},
  cellClassNames = {},
  className,
}: SimpleTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [activeDrilldown, setActiveDrilldown] = React.useState<DrilldownCell | null>(null)
  const [activeMapLocation, setActiveMapLocation] = React.useState<MapLocation | null>(null)

  const headersToRender = React.useMemo(() => {
    // A header with no key yields a column with no accessorKey, and since our header is a
    // render function rather than a string, react-table then has no id to fall back on and
    // throws for the whole table. Dropping the column loses one heading; keeping it loses
    // the insight.
    const normalizedHeaders = normalizeHeaders(headers).filter((header) => header.key !== '')
    const htr = keysToIgnore?.length
      ? normalizedHeaders.filter((header) => !keysToIgnore.includes(header.key))
      : normalizedHeaders
    return htr
  }, [keysToIgnore, headers])

  const columns = useMemo<ColumnDef<SimpleTableBodyType>[]>(
    () =>
      (headersToRender || []).map((header) => ({
        accessorKey: header.key,
        header: () => {
          // Don't show anything for placeholder columns
          if (header.key.includes('placeholder')) {
            return ''
          }

          const text = header.title || startCase(header.key)
          // If we have an icon, show both icon and title
          if (header.icon) {
            return (
              <div className="flex items-center justify-center gap-2">
                {header.icon}
                <span>{text}</span>
              </div>
            )
          }
          // Default behavior - just show the formatted title
          return text
        },

        cell: ({ getValue }) => RenderCell(getValue(), setActiveDrilldown, setActiveMapLocation),

        sortingFn: createSortingFn(header.key),
      })),
    [headersToRender]
  )

  const tableData = useMemo(() => {
    if (body && body.length > 0) {
      return body
    }

    return Array(5)
      .fill(null)
      .map(() =>
        (headersToRender || []).reduce((acc, header) => {
          acc[header.key] = ''
          return acc
        }, {} as SimpleTableBodyType)
      )
  }, [body, headersToRender])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (!headersToRender || headersToRender.length === 0) return null

  const headBaseClasses = 'bg-base-200 text-base-content'
  const headVariationStyles = {
    [TableVariationStyle.secondary]: 'bg-neutral text-neutral-content border',
  }

  return (
    <section className=" max-h-full overflow-auto ">
      {activeDrilldown && (
        <Drilldown configuration={activeDrilldown.configuration} onClose={() => setActiveDrilldown(null)} />
      )}
      {activeMapLocation && (
        <MapModal
          show={!!activeMapLocation}
          onHide={() => setActiveMapLocation(null)}
          location={activeMapLocation}
          title={activeMapLocation.title || 'Location'}
          markerContent={activeMapLocation.markerContent}
        />
      )}
      {title && <h4 className={cx('leading-6 ')}>{title}</h4>}
      <div className={cx(' h-full min-h-0', className)}>
        <table className="table" aria-label="props table">
          <thead className="sticky top-0 z-10 px-2">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cx(
                      headBaseClasses,
                      variation ? headVariationStyles[variation] : '',
                      headerClassNames[header.id] || '',
                      'text-center py-2 font-medium ',
                      header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-opacity-80' : ''
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center justify-start gap-1">
                      <span key="flex">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                      {header.column.getCanSort() && (
                        <span key="sort" className="text-xs px-2">
                          {{
                            asc: <LuChevronUp className="text-sm" aria-hidden />,
                            desc: <LuChevronDown className="text-sm" aria-hidden />,
                          }[header.column.getIsSorted() as string] ?? (
                            <LuChevronsUpDown className="text-sm" aria-hidden />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    data-key={cell.id}
                    className={cx(
                      'text-sm text-left p-2 font-normal',
                      !body || body.length === 0 ? 'text-transparent' : '',
                      cellClassNames ? cellClassNames[cell.column.id] : ''
                    )}
                    aria-label={!body || body.length === 0 ? 'no data' : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
